// @flow
import { get } from 'lodash/fp';
import * as devices from './devices';
import * as general from './general';
import * as React from 'react';
import * as weather from './weather';

import type { Element } from 'react';
import type { AnyIcon } from '~/core/constants/icons';
import type { DevicesIcon } from '~/core/constants/icons/devices-svg';
import type { DeviceModel, DeviceType, Region } from '~/core/types/devices';
import { deviceModels, deviceTypes, regions } from '~/core/constants/devices';
import devicesIcons from '~/core/constants/icons/devices-svg';
import generalIcons from '~/core/constants/icons/general-svg';
import weatherIcons from '~/core/constants/icons/weather-svg';

const deviceIconMap: { [DeviceType]: DevicesIcon } = {
    [deviceTypes.COLOR_TUNEABLE_LIGHT]: devicesIcons.LIGHT,
    [deviceTypes.COLOR_LIGHT]: devicesIcons.LIGHT,
    [deviceTypes.CONTACT_SENSOR]: devicesIcons.DOOR_SENSOR,
    [deviceTypes.HEATING]: devicesIcons.HEATING,
    [deviceTypes.HIVE_BUTTON]: devicesIcons.HIVE_BUTTON,
    [deviceTypes.HIVE_CAMERA]: devicesIcons.CAMERA_DEHARO,
    [deviceTypes.HOT_WATER]: devicesIcons.HOT_WATER,
    [deviceTypes.LEAK_SENSOR]: devicesIcons.LEAK_SENSOR,
    [deviceTypes.MOTION_SENSOR]: devicesIcons.MOTION_SENSOR,
    [deviceTypes.NA_THERMOSTAT]: devicesIcons.FLAME_NA,
    [deviceTypes.SIGNAL_BOOSTER]: devicesIcons.SIGNAL_BOOST,
    [deviceTypes.SENSE]: devicesIcons.SENSE,
    [deviceTypes.TUNEABLE_LIGHT]: devicesIcons.LIGHT,
    [deviceTypes.WARM_WHITE_LIGHT]: devicesIcons.LIGHT
};

const regionIconMap: { [DeviceType]: { [Region]: DevicesIcon } } = {
    [deviceTypes.ACTIVE_PLUG]: {
        [regions.EU]: devicesIcons.PLUG_EU,
        [regions.IN]: devicesIcons.PLUG_IN,
        [regions.NA]: devicesIcons.PLUG_NA,
        [regions.UK]: devicesIcons.PLUG_UK
    },
    [deviceTypes.SMART_PLUG]: {
        [regions.EU]: devicesIcons.PLUG_EU,
        [regions.IN]: devicesIcons.PLUG_IN,
        [regions.NA]: devicesIcons.PLUG_NA,
        [regions.UK]: devicesIcons.PLUG_UK
    }
};

const productGroupMap: { [string]: DevicesIcon } = {
    [deviceTypes.PRODUCT_GROUP]: generalIcons.GROUP_CONTROL
};

const modelIconMap: {
    [DeviceType]: {
        [DeviceModel]: DevicesIcon
    }
} = {
    [deviceTypes.BOILER_MODULE]: {
        [deviceModels.SLR1]: devicesIcons.BOILER_MODULE_SLR1,
        [deviceModels.SLR2]: devicesIcons.BOILER_MODULE_SLR2
    },
    [deviceTypes.HUB]: {
        [deviceModels.NANO1]: devicesIcons.HUB_NANO1,
        [deviceModels.NANO2]: devicesIcons.HUB_NANO2,
        [deviceModels.SENSE]: devicesIcons.SENSE
    },
    [deviceTypes.THERMOSTAT]: {
        [deviceModels.SLT1]: devicesIcons.THERMOSTAT_SLT1,
        [deviceModels.SLT2]: devicesIcons.THERMOSTAT_SLT2,
        [deviceModels.SLT3]: devicesIcons.THERMOSTAT_SLT3,
        [deviceModels.SLT3b]: devicesIcons.THERMOSTAT_SLT3
    },
    [deviceTypes.THERMOSTAT_UI]: {
        [deviceModels.SLT1]: devicesIcons.THERMOSTAT_SLT1,
        [deviceModels.SLT2]: devicesIcons.THERMOSTAT_SLT2,
        [deviceModels.SLT3]: devicesIcons.THERMOSTAT_SLT3,
        [deviceModels.SLT3b]: devicesIcons.THERMOSTAT_SLT3
    },
    [deviceTypes.ACTIVE_PLUG]: {
        [deviceModels.SLP2d]: devicesIcons.PLUG_EU,
        [deviceModels.SLP2c]: devicesIcons.PLUG_NA,
        [deviceModels.SLP2b]: devicesIcons.PLUG_UK,
        [deviceModels.SLP2]: devicesIcons.PLUG_UK
    }
};

export const productTypeToIcon = ({
    localeSuffix = regions.UK,
    model = '',
    type
}: {
    localeSuffix?: Region,
    model?: DeviceModel,
    type: DeviceType
}): DevicesIcon => {
    if (type in deviceIconMap) {
        return deviceIconMap[type];
    }

    if (type in productGroupMap) {
        return productGroupMap[type];
    }

    const iconFromModel = get([type, model])(modelIconMap);

    if (iconFromModel) {
        return iconFromModel;
    }

    const iconFromRegion = get([type, localeSuffix])(regionIconMap);

    if (iconFromRegion) {
        return iconFromRegion;
    }

    return devicesIcons.LINKED;
};

export const map: { [AnyIcon]: Element } = {
    [devicesIcons.ACTIVE_BUTTON]: devices.ActionButton,
    [devicesIcons.APPLIANCE]: devices.Appliance,
    [devicesIcons.BOILER_MODULE_SLR1]: devices.BoilerModuleSLR1,
    [devicesIcons.BOILER_MODULE_SLR2]: devices.BoilerModuleSLR2,
    [devicesIcons.BUTTON_OFF]: devices.ButtonOff,
    [devicesIcons.CAMERA_DEHARO]: devices.CameraDeharo,
    [devicesIcons.CAMERA_OFF]: devices.CameraOff,
    [devicesIcons.COLOUR_PICKER]: devices.ColourPicker,
    [devicesIcons.COOLING]: devices.Cooling,
    [devicesIcons.DOOR_SENSOR]: devices.DoorSensorClosed,
    [devicesIcons.DOUBLE_TAP]: devices.DoubleTap,
    [devicesIcons.DUO_SOCKET_EU]: devices.DuoSocketEU,
    [devicesIcons.DUO_SOCKET_IN]: devices.DuoSocketIN,
    [devicesIcons.DUO_SOCKET_NA]: devices.DuoSocketNA,
    [devicesIcons.DUO_SOCKET_UK]: devices.DuoSocketUK,
    [devicesIcons.FAN_OFF]: devices.FanOff,
    [devicesIcons.FAN_ON]: devices.FanOn,
    [devicesIcons.FLAME_NA]: devices.FlameNA,
    [devicesIcons.GENERIC_TEMP]: devices.GenericTemp,
    [devicesIcons.HEATING]: devices.Heating,
    [devicesIcons.HIVE_BUTTON]: devices.HiveButton,
    [devicesIcons.HOT_WATER]: devices.HotWater,
    [devicesIcons.HOT_WATER_ON]: devices.HotWaterOn,
    [devicesIcons.HUB_NANO1]: devices.HubNano1,
    [devicesIcons.HUB_NANO2]: devices.HubNano2,
    [devicesIcons.HUMIDITY]: devices.Humidity,
    [devicesIcons.LEAK_SENSOR]: devices.LeakSensor,
    [devicesIcons.LIGHT]: devices.Light,
    [devicesIcons.LIGHT_DOUBLE]: devices.LightDouble,
    [devicesIcons.LINK_BROKEN]: devices.LinkBroken,
    [devicesIcons.LINKED]: devices.Linked,
    [devicesIcons.LONG_PRESS]: devices.LongPress,
    [devicesIcons.MOTION_SENSOR]: devices.MotionSensor,
    [devicesIcons.ONE_TAP]: devices.OneTap,
    [devicesIcons.PHILIPS_HUE_BRIDGE]: devices.PhilipsHueBridge,
    [devicesIcons.PLUG_EU]: devices.PlugEU,
    [devicesIcons.PLUG_IN]: devices.PlugIN,
    [devicesIcons.PLUG_NA]: devices.PlugNA,
    [devicesIcons.PLUG_UK]: devices.PlugUK,
    [devicesIcons.SENSE]: devices.Sense,
    [devicesIcons.SIGNAL_BOOST]: devices.SignalBoost,
    [devicesIcons.SIGNAL_BOOSTER_PLUG]: devices.SignalBoosterPlug,
    [devicesIcons.SOCKET_EU]: devices.SocketEU,
    [devicesIcons.SOCKET_IN]: devices.SocketIN,
    [devicesIcons.SOCKET_NA]: devices.SocketNA,
    [devicesIcons.SOCKET_UK]: devices.SocketUK,
    [devicesIcons.SWITCH]: devices.Switch,
    [devicesIcons.TANKS]: devices.Tanks,
    [devicesIcons.THERMOSTAT_SLT1]: devices.ThermostatSLT1,
    [devicesIcons.THERMOSTAT_SLT2]: devices.ThermostatSLT2,
    [devicesIcons.THERMOSTAT_SLT3]: devices.ThermostatSLT3,
    [devicesIcons.TOILETS]: devices.Toilets,
    [devicesIcons.WATER_FLOWING]: devices.WaterFlowing,
    [devicesIcons.WATER_NOT_FLOWING]: devices.WaterNotFlowing,
    // **** GENERAL ICONS ****
    [generalIcons.ACTIONS]: general.Actions,
    [generalIcons.ADD_DEVICES]: general.AddDevices,
    [generalIcons.ARROW_DOWN]: general.ArrowDown,
    [generalIcons.ARROW_LEFT]: general.ArrowLeft,
    [generalIcons.ARROW_LEFT_SHAFT]: general.ArrowLeftShaft,
    [generalIcons.ARROW_RIGHT]: general.ArrowRight,
    [generalIcons.ARROW_RIGHT_DOUBLE]: general.ArrowRightDouble,
    [generalIcons.ARROW_UP]: general.ArrowUp,
    [generalIcons.BATTERY_EMPTY]: general.BatteryEmpty,
    [generalIcons.BATTERY_FULL]: general.BatteryFull,
    [generalIcons.BATTERY_LOW]: general.BatteryLow,
    [generalIcons.BATTERY_NORMAL]: general.BatteryNormal,
    [generalIcons.BOOST]: general.Boost,
    [generalIcons.BORD_GAIS_LOGO]: general.BordGaisLogo,
    [generalIcons.CALL]: general.Call,
    [generalIcons.CIRCLE_FILLED]: general.CircleFilled,
    [generalIcons.CIRCLE_OUTLINE]: general.CircleOutline,
    [generalIcons.CLOSE]: general.Close,
    [generalIcons.CLOSE_HEAVY]: general.CloseHeavy,
    [generalIcons.COLOR_PALETE]: general.ColorPalete,
    [generalIcons.COLOR_PICKER]: general.ColorPicker,
    [generalIcons.COMFORT]: general.Comfort,
    [generalIcons.CONNECTED]: general.Connected,
    [generalIcons.CONTROL_PAGE]: general.ControlPage,
    [generalIcons.COPY]: general.Copy,
    [generalIcons.CREATE]: general.Create,
    [generalIcons.CREATE_PLUS]: general.CreatePlus,
    [generalIcons.DASHBOARD]: general.Dashboard,
    [generalIcons.DEMO]: general.Demo,
    [generalIcons.DIMMER]: general.Dimmer,
    [generalIcons.DISCOVER]: general.Discover,
    [generalIcons.DOG_BARK]: general.DogBark,
    [generalIcons.DOWNLOAD]: general.Download,
    [generalIcons.EDIT]: general.Edit,
    [generalIcons.EFFICIENCY]: general.Efficiency,
    [generalIcons.EXCLAMATION]: general.Exclamation,
    [generalIcons.FROST_PROTECTION]: general.FrostProtection,
    [generalIcons.GLASS_BREAK]: general.GlassBreak,
    [generalIcons.GROUP_CONTROL]: general.GroupControl,
    [generalIcons.GRAPHS]: general.Graphs,
    [generalIcons.HEATING_PAGE]: general.HeatingPage,
    [generalIcons.HELP]: general.Help,
    [generalIcons.HIVE_ICON]: general.HiveIcon,
    [generalIcons.HOLIDAYS]: general.Holidays,
    [generalIcons.HOME]: general.Home,
    [generalIcons.HOT_WATER]: general.HotWater,
    [generalIcons.INCORRECT_SOUND]: general.IncorrectSound,
    [generalIcons.INFO]: general.Info,
    [generalIcons.INSTALL_DEVICE]: general.InstallDevice,
    [generalIcons.LINK]: general.Link,
    [generalIcons.LINK_BROKEN]: general.LinkBroken,
    [generalIcons.LIST_DEVICE_MENU]: general.ListDeviceMenu,
    [generalIcons.LOCK_CLOSED]: general.LockClosed,
    [generalIcons.LOGOUT]: general.Logout,
    [generalIcons.MANAGE_DEVICE]: general.ManageDevice,
    [generalIcons.MENU]: general.Menu,
    [generalIcons.MICROPHONE_OFF]: general.MicrophoneOff,
    [generalIcons.MICROPHONE_ON]: general.MicrophoneOn,
    [generalIcons.MIMIC]: general.Mimic,
    [generalIcons.MINUS]: general.Minus,
    [generalIcons.MONITOR_SENSE]: general.MonitorSense,
    [generalIcons.MONITORING_OFF]: general.MonitoringOff,
    [generalIcons.MONITORING_ON]: general.MonitoringOn,
    [generalIcons.MOODS]: general.Moods,
    [generalIcons.MORE]: general.More,
    [generalIcons.MORE_VERTICAL]: general.MoreVertical,
    [generalIcons.MULTI_PROFILE]: general.MultiProfile,
    [generalIcons.NOTIFICATIONS]: general.Notifications,
    [generalIcons.NOTIFICATIONS_OFF]: general.NotificationsOff,
    [generalIcons.OVERFLOW]: general.Overflow,
    [generalIcons.PAUSE]: general.Pause,
    [generalIcons.PAUSE_SIMPLE]: general.PauseSimple,
    [generalIcons.PLAY]: general.Play,
    [generalIcons.PLAY_SIMPLE]: general.PlaySimple,
    [generalIcons.PLUS]: general.Plus,
    [generalIcons.QUESTION]: general.Question,
    [generalIcons.REASSURANCE]: general.Reassurance,
    [generalIcons.RECIPES]: general.Recipes,
    [generalIcons.RESET_RELOAD]: general.ResetReload,
    [generalIcons.SCHEDULE]: general.Schedule,
    [generalIcons.SEARCH]: general.Search,
    [generalIcons.SETTINGS]: general.Settings,
    [generalIcons.SHOP]: general.Shop,
    [generalIcons.SIGNAL_1_BARS]: general.Signal1Bars,
    [generalIcons.SIGNAL_2_BARS]: general.Signal2Bars,
    [generalIcons.SIGNAL_3_BARS]: general.Signal3Bars,
    [generalIcons.SIGNAL_4_BARS]: general.Signal4Bars,
    [generalIcons.SMOKE_CO]: general.SmokeCo,
    [generalIcons.SMOKE_CO2]: general.SmokeCo2,
    [generalIcons.SOUND_CAM]: general.SoundCam,
    [generalIcons.SOUND_OFF]: general.SoundOff,
    [generalIcons.SOUND_ON]: general.SoundOn,
    [generalIcons.STOP]: general.Stop,
    [generalIcons.STOP_SIMPLE]: general.StopSimple,
    [generalIcons.TICK_ONE]: general.TickOne,
    [generalIcons.TICK_SIMPLE]: general.TickSimple,
    [generalIcons.TONE]: general.Tone,
    [generalIcons.TRASH]: general.Trash,
    [generalIcons.USER_PROFILE]: general.UserProfile,
    [generalIcons.WELCOME_HOME]: general.WelcomeHome,
    [generalIcons.WIFI]: general.Wifi,
    [generalIcons.WIFI_0_BARS]: general.Wifi0Bars,
    [generalIcons.WIFI_1_BARS]: general.Wifi1Bars,
    [generalIcons.WIFI_2_BARS]: general.Wifi2Bars,
    [generalIcons.WIFI_3_BARS]: general.Wifi3Bars,
    [generalIcons.WIFI_OFF]: general.WifiOff,
    // **** WEATHER ICONS ****
    [weatherIcons.BLACK_LOW_CLOUD]: weather.BlackLowCloud,
    [weatherIcons.CLEAR_SKY]: weather.ClearSky,
    [weatherIcons.CLOUDY_WITH_HEAVY_RAIN]: weather.CloudyWithHeavyRain,
    [weatherIcons.CLOUDY_WITH_HEAVY_RAIN_NIGHT]: weather.CloudyWithHeavyRainNight,
    [weatherIcons.CLOUDY_WITH_HEAVY_SNOW]: weather.CloudyWithHeavySnow,
    [weatherIcons.CLOUDY_WITH_LIGHT_RAIN]: weather.CloudyWithLightRain,
    [weatherIcons.CLOUDY_WITH_LIGHT_RAIN_NIGHT]: weather.CloudyWithLightRainNight,
    [weatherIcons.CLOUDY_WITH_SLEET]: weather.CloudyWithSleet,
    [weatherIcons.FOG_MIST]: weather.FogMist,
    [weatherIcons.HEAVY_RAIN_SHOWERS]: weather.HeavyRainShowers,
    [weatherIcons.HEAVY_SNOW_SHOWERS]: weather.HeavySnowShowers,
    [weatherIcons.HEAVY_SNOW_SHOWERS_NIGHT]: weather.HeavySnowShowersNight,
    [weatherIcons.LIGHT_RAIN_SHOWERS]: weather.LightRainShowers,
    [weatherIcons.LIGHT_SNOW]: weather.LightSnow,
    [weatherIcons.LIGHT_SNOW_NIGHT]: weather.LightSnowNight,
    [weatherIcons.LIGHT_SNOW_SHOWERS]: weather.LightSnowShowers,
    [weatherIcons.SLEET_SHOWERS]: weather.SleetShowers,
    [weatherIcons.SLEET_SHOWERS_NIGHT]: weather.SleetShowersNight,
    [weatherIcons.SUNNY]: weather.Sunny,
    [weatherIcons.SUNNY_INTERVALS]: weather.SunnyIntervals,
    [weatherIcons.SUNSET_SUNRISE]: weather.SunsetSunrise,
    [weatherIcons.THUNDER_SHOWERS]: weather.ThunderShowers,
    [weatherIcons.THUNDER_SHOWERS_NIGHT]: weather.ThunderShowersNight,
    [weatherIcons.THUNDERSTORMS]: weather.Thunderstorms,
    [weatherIcons.WHITE_CLOUD]: weather.WhiteCloud,
    [weatherIcons.WHITE_CLOUD_NIGHT]: weather.WhiteCloudNight
};

export default function getIconComponent(type: AnyIcon, props?: Object = {}): ?React$Element<*> {
    return map[type] ? React.createElement(map[type], props) : null;
}



// WEBPACK FOOTER //
// ./src/core/icons/svg-icons/map.js